import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  Card,
  CardHeader,
  CardBody,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Progress,
  HStack,
  Button,
  Select,
  useColorModeValue,
  Spinner,
  Divider,
  Avatar,
  AvatarGroup,
  Tooltip,
  CircularProgress,
  CircularProgressLabel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import {
  FiUsers,
  FiUserPlus,
  FiTrendingUp,
  FiBarChart2,
  FiPieChart,
  FiDollarSign,
  FiTarget,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiRefreshCw,
  FiMapPin,
  FiArrowUpRight,
  FiArrowDownRight,
  FiCalendar,
  FiSmile,
  FiStar,
  FiTrendingDown,
  FiActivity,
  FiShoppingBag,
  FiPackage,
} from 'react-icons/fi';
import { 
  MdOutlineAttachMoney, 
  MdOutlineCampaign, 
  MdOutlineCategory,
  MdOutlineShowChart,
  MdOutlineStorefront,
  MdOutlineStore,
  MdOutlineLeaderboard,
  MdTimeline,
} from 'react-icons/md';
import api from '../../utils/api';

// Reusable components
const StatCard = ({ title, value, icon, change, colorScheme, formatter, isLoading, footerText }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const iconBg = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.200`);
  const iconColor = useColorModeValue('white', 'gray.800');

  const displayValue = formatter ? formatter(value) : value;

  return (
    <Card boxShadow="md" bg={cardBg}>
      <CardBody p={4}>
        <Flex alignItems="center">
          <Box
            bg={iconBg}
            color={iconColor}
            p={3}
            borderRadius="lg"
            mr={4}
            boxShadow="md"
          >
            <Icon as={icon} boxSize={6} />
          </Box>
          <Box flex="1">
            <Text fontSize="sm" color="gray.500" fontWeight="medium">
              {title}
            </Text>
            {isLoading ? (
              <Spinner size="sm" mt={1} colorScheme={colorScheme} />
            ) : (
              <Text fontSize="2xl" fontWeight="bold">
                {displayValue}
              </Text>
            )}
            {change !== undefined && (
              <Stat>
                <StatHelpText mb={0}>
                  <StatArrow type={change > 0 ? 'increase' : 'decrease'} />
                  {Math.abs(change)}%
                </StatHelpText>
              </Stat>
            )}
            {footerText && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                {footerText}
              </Text>
            )}
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('pt-BR').format(value);
};

// Main Dashboard Component
const ComercialDashboard = () => {
  const [timeFrame, setTimeFrame] = useState('month');
  const [isLoading, setIsLoading] = useState(true);
  const [salesStats, setSalesStats] = useState({
    revenue: { value: 0, change: 0 },
    newCustomers: { value: 0, change: 0 },
    newProviders: { value: 0, change: 0 },
    conversionRate: { value: 0, change: 0 },
    avgTicket: { value: 0, change: 0 },
    totalServices: { value: 0, change: 0 },
  });
  const [topCategories, setTopCategories] = useState([]);
  const [salesByRegion, setSalesByRegion] = useState([]);
  const [topProviders, setTopProviders] = useState([]);
  const [salesGoals, setSalesGoals] = useState({
    target: 0,
    current: 0,
    percentage: 0,
  });

  useEffect(() => {
    // Simulating API call
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // In a real implementation, this would be an API call
        // const response = await api.get('api/dashboard/comercial');
        // const data = await response.json();
        
        // Mock data for demo
        setTimeout(() => {
          setSalesStats({
            revenue: { value: 456789.50, change: 15 },
            newCustomers: { value: 238, change: 8 },
            newProviders: { value: 62, change: 12 },
            conversionRate: { value: 8.5, change: 3 },
            avgTicket: { value: 285.40, change: 5 },
            totalServices: { value: 1845, change: 18 },
          });
          
          // Mock top categories
          setTopCategories([
            { id: 1, name: 'Limpeza Residencial', revenue: 145670.80, count: 580, growth: 22 },
            { id: 2, name: 'Assistência Técnica', revenue: 98450.60, count: 324, growth: 15 },
            { id: 3, name: 'Reformas', revenue: 86320.40, count: 112, growth: 18 },
            { id: 4, name: 'Serviços Elétricos', revenue: 72540.90, count: 286, growth: 10 },
            { id: 5, name: 'Instalações', revenue: 53807.80, count: 543, growth: 8 },
          ]);
          
          // Mock sales by region
          setSalesByRegion([
            { region: 'Sul', percentage: 24, value: 109629.48, growth: 12 },
            { region: 'Sudeste', percentage: 38, value: 173580.01, growth: 18 },
            { region: 'Centro-oeste', percentage: 15, value: 68518.43, growth: 5 },
            { region: 'Nordeste', percentage: 18, value: 82222.11, growth: 22 },
            { region: 'Norte', percentage: 5, value: 22839.48, growth: 8 },
          ]);
          
          // Mock top providers
          setTopProviders([
            { 
              id: 1, 
              name: 'Carlos Ferreira', 
              avatar: 'https://bit.ly/placeholder-avatar',
              category: 'Assistência Técnica',
              completed: 38,
              rating: 4.8,
              revenue: 12450.80,
            },
            { 
              id: 2, 
              name: 'Mariana Silva', 
              avatar: 'https://bit.ly/placeholder-avatar',
              category: 'Limpeza Residencial',
              completed: 45,
              rating: 4.9,
              revenue: 11280.50,
            },
            { 
              id: 3, 
              name: 'Ricardo Oliveira', 
              avatar: 'https://bit.ly/placeholder-avatar',
              category: 'Serviços Elétricos',
              completed: 32,
              rating: 4.7,
              revenue: 9870.30,
            },
            { 
              id: 4, 
              name: 'Juliana Santos', 
              avatar: 'https://bit.ly/placeholder-avatar',
              category: 'Reformas',
              completed: 24,
              rating: 4.6,
              revenue: 8750.20,
            },
            { 
              id: 5, 
              name: 'Felipe Costa', 
              avatar: 'https://bit.ly/placeholder-avatar',
              category: 'Instalações',
              completed: 29,
              rating: 4.5,
              revenue: 7920.40,
            },
          ]);
          
          // Mock sales goals
          setSalesGoals({
            target: 500000,
            current: 456789.50,
            percentage: 91.36,
          });
          
          setIsLoading(false);
        }, 1500);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [timeFrame]);

  const handleTimeFrameChange = (e) => {
    setTimeFrame(e.target.value);
  };

  const cardBg = useColorModeValue('white', 'gray.700');
  const tableBg = useColorModeValue('gray.50', 'gray.700');
  const lightBg = useColorModeValue('gray.50', 'gray.600');

  return (
    <Box w="100%" p={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">Dashboard Comercial</Heading>
          <Text color="gray.500">Análise de vendas e desempenho comercial</Text>
        </Box>
        <HStack>
          <Text mr={2}>Período:</Text>
          <Select value={timeFrame} onChange={handleTimeFrameChange} w="150px">
            <option value="day">Hoje</option>
            <option value="week">Esta Semana</option>
            <option value="month">Este Mês</option>
            <option value="quarter">Este Trimestre</option>
            <option value="year">Este Ano</option>
          </Select>
          <Button 
            leftIcon={<FiRefreshCw />} 
            colorScheme="yellow" 
            variant="outline"
            onClick={() => {
              setIsLoading(true);
              setTimeout(() => setIsLoading(false), 1000);
            }}
          >
            Atualizar
          </Button>
        </HStack>
      </Flex>

      {/* Sales Progress */}
      <Card bg={cardBg} boxShadow="md" mb={6}>
        <CardBody>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            alignItems={{ base: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <HStack spacing={6} mb={{ base: 4, md: 0 }} flexGrow={1}>
              <Box>
                <CircularProgress 
                  value={salesGoals.percentage} 
                  size="100px" 
                  thickness="8px" 
                  color="green.400"
                >
                  <CircularProgressLabel>{salesGoals.percentage.toFixed(1)}%</CircularProgressLabel>
                </CircularProgress>
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="lg">Meta de {timeFrame === 'year' ? 'Ano' : 'Mês'}</Text>
                <Text fontSize="sm" color="gray.500" mb={2}>
                  {isLoading ? <Spinner size="xs" /> : formatCurrency(salesGoals.current)} de {isLoading ? <Spinner size="xs" /> : formatCurrency(salesGoals.target)}
                </Text>
                <Progress value={salesGoals.percentage} size="sm" colorScheme="green" borderRadius="full" />
              </Box>
            </HStack>
            
            <Grid 
              templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }} 
              gap={4}
              alignItems="center"
              flexGrow={1}
            >
              <Stat>
                <StatLabel fontSize="xs">Meta Mensal</StatLabel>
                <StatNumber fontSize="md">{isLoading ? <Spinner size="xs" /> : formatCurrency(500000)}</StatNumber>
                <StatHelpText fontSize="xs">Maio 2025</StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel fontSize="xs">Média Diária</StatLabel>
                <StatNumber fontSize="md">{isLoading ? <Spinner size="xs" /> : formatCurrency(15226.32)}</StatNumber>
                <StatHelpText fontSize="xs">
                  <StatArrow type="increase" />
                  12%
                </StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel fontSize="xs">Projeção</StatLabel>
                <StatNumber fontSize="md">{isLoading ? <Spinner size="xs" /> : formatCurrency(512000)}</StatNumber>
                <StatHelpText fontSize="xs">
                  <StatArrow type="increase" />
                  102.4% da meta
                </StatHelpText>
              </Stat>
            </Grid>
          </Flex>
        </CardBody>
      </Card>

      {/* Main Stats */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Receita Total" 
          value={salesStats.revenue.value} 
          icon={FiDollarSign} 
          change={salesStats.revenue.change} 
          colorScheme="green" 
          formatter={formatCurrency}
          isLoading={isLoading} 
          footerText="No período selecionado"
        />
        <StatCard 
          title="Novos Clientes" 
          value={salesStats.newCustomers.value} 
          icon={FiUserPlus} 
          change={salesStats.newCustomers.change} 
          colorScheme="blue" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="Contratantes cadastrados"
        />
        <StatCard 
          title="Novos Prestadores" 
          value={salesStats.newProviders.value} 
          icon={FiUsers} 
          change={salesStats.newProviders.change} 
          colorScheme="teal" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="Contratados cadastrados"
        />
      </Grid>

      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Taxa de Conversão" 
          value={`${salesStats.conversionRate.value}%`} 
          icon={FiTarget} 
          change={salesStats.conversionRate.change} 
          colorScheme="orange" 
          isLoading={isLoading}
          footerText="Visitantes → Clientes"
        />
        <StatCard 
          title="Ticket Médio" 
          value={salesStats.avgTicket.value} 
          icon={MdOutlineAttachMoney} 
          change={salesStats.avgTicket.change} 
          colorScheme="purple" 
          formatter={formatCurrency}
          isLoading={isLoading}
          footerText="Valor médio por serviço"
        />
        <StatCard 
          title="Total de Serviços" 
          value={salesStats.totalServices.value} 
          icon={FiPackage} 
          change={salesStats.totalServices.change} 
          colorScheme="yellow" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="Serviços realizados"
        />
      </Grid>

      {/* Top Categories & Sales by Region */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "3fr 2fr" }} gap={6} mb={6}>
        <Card bg={cardBg} boxShadow="md">
          <CardHeader>
            <Heading size="md">Top Categorias</Heading>
          </CardHeader>
          <CardBody px={{ base: 2, md: 4 }}>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Table variant="simple" size="sm">
                <Thead bg={tableBg}>
                  <Tr>
                    <Th>Categoria</Th>
                    <Th isNumeric>Receita</Th>
                    <Th isNumeric>Serviços</Th>
                    <Th isNumeric>Crescimento</Th>
                    <Th>Performance</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {topCategories.map(category => (
                    <Tr key={category.id}>
                      <Td fontWeight="medium">{category.name}</Td>
                      <Td isNumeric>{formatCurrency(category.revenue)}</Td>
                      <Td isNumeric>{formatNumber(category.count)}</Td>
                      <Td isNumeric>
                        <Flex justify="flex-end" align="center">
                          <Icon 
                            as={category.growth > 0 ? FiTrendingUp : FiTrendingDown} 
                            color={category.growth > 0 ? 'green.500' : 'red.500'} 
                            mr={1} 
                          />
                          <Text 
                            color={category.growth > 0 ? 'green.500' : 'red.500'} 
                            fontWeight="medium"
                          >
                            {category.growth}%
                          </Text>
                        </Flex>
                      </Td>
                      <Td>
                        <Progress 
                          value={category.revenue / 2000} 
                          size="xs" 
                          colorScheme={
                            category.growth > 15 ? 'green' : 
                            category.growth > 10 ? 'teal' : 
                            category.growth > 5 ? 'blue' : 'yellow'
                          } 
                          borderRadius="full"
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </CardBody>
        </Card>

        <Card bg={cardBg} boxShadow="md">
          <CardHeader>
            <Heading size="md">Vendas por Região</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={4}>
                {salesByRegion.map((region, index) => (
                  <Box key={index}>
                    <Flex justify="space-between" mb={1}>
                      <HStack>
                        <Icon as={FiMapPin} color={
                          index === 0 ? 'blue.500' :
                          index === 1 ? 'green.500' :
                          index === 2 ? 'purple.500' :
                          index === 3 ? 'orange.500' : 'red.500'
                        } />
                        <Text fontWeight="medium">{region.region}</Text>
                      </HStack>
                      <HStack>
                        <Text fontWeight="medium">{region.percentage}%</Text>
                        <Badge colorScheme={region.growth > 15 ? 'green' : 'blue'}>
                          +{region.growth}%
                        </Badge>
                      </HStack>
                    </Flex>
                    <Progress 
                      value={region.percentage} 
                      size="sm" 
                      colorScheme={
                        index === 0 ? 'blue' :
                        index === 1 ? 'green' :
                        index === 2 ? 'purple' :
                        index === 3 ? 'orange' : 'red'
                      } 
                      borderRadius="full"
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {formatCurrency(region.value)}
                    </Text>
                  </Box>
                ))}

                <Divider />

                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.600">Total de 5 regiões</Text>
                  <Text fontSize="sm" fontWeight="bold">{formatCurrency(salesStats.revenue.value)}</Text>
                </HStack>
              </Stack>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* Top Providers & Sales Performance */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "1fr 1fr" }} gap={6} mb={6}>
        <Card bg={cardBg} boxShadow="md">
          <CardHeader>
            <Heading size="md">Top Prestadores</Heading>
          </CardHeader>
          <CardBody px={{ base: 2, md: 4 }}>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Table variant="simple" size="sm">
                <Thead bg={tableBg}>
                  <Tr>
                    <Th>Prestador</Th>
                    <Th>Categoria</Th>
                    <Th isNumeric>Serviços</Th>
                    <Th isNumeric>Avaliação</Th>
                    <Th isNumeric>Faturamento</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {topProviders.map(provider => (
                    <Tr key={provider.id}>
                      <Td>
                        <Flex align="center">
                          <Avatar size="sm" name={provider.name} src={provider.avatar} mr={2} />
                          <Text fontWeight="medium">{provider.name}</Text>
                        </Flex>
                      </Td>
                      <Td>{provider.category}</Td>
                      <Td isNumeric>{provider.completed}</Td>
                      <Td isNumeric>
                        <HStack justify="flex-end">
                          <Text 
                            color={
                              provider.rating >= 4.8 ? 'green.500' :
                              provider.rating >= 4.5 ? 'teal.500' :
                              provider.rating >= 4.0 ? 'blue.500' : 'orange.500'
                            }
                            fontWeight="medium"
                          >
                            {provider.rating}
                          </Text>
                          <Icon as={FiStar} color="yellow.500" fontSize="sm" />
                        </HStack>
                      </Td>
                      <Td isNumeric fontWeight="medium">
                        {formatCurrency(provider.revenue)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
            <Button 
              mt={4} 
              colorScheme="yellow" 
              variant="outline" 
              size="sm" 
              mx="auto" 
              display="block"
            >
              Ver Todos os Prestadores
            </Button>
          </CardBody>
        </Card>

        <Card bg={cardBg} boxShadow="md">
          <CardHeader>
            <Heading size="md">Desempenho de Vendas</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Tabs colorScheme="yellow" isFitted>
                <TabList>
                  <Tab>Visão Geral</Tab>
                  <Tab>Tendências</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
                    <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }} gap={6}>
                      <Box>
                        <Text fontWeight="medium" mb={2}>Desempenho por Serviço</Text>
                        <Stack spacing={3}>
                          <HStack justify="space-between">
                            <HStack>
                              <Icon as={MdOutlineCategory} color="blue.500" />
                              <Text fontSize="sm">Limpeza</Text>
                            </HStack>
                            <Text fontSize="sm" fontWeight="medium">32%</Text>
                          </HStack>
                          <HStack justify="space-between">
                            <HStack>
                              <Icon as={MdOutlineCategory} color="green.500" />
                              <Text fontSize="sm">Técnico</Text>
                            </HStack>
                            <Text fontSize="sm" fontWeight="medium">22%</Text>
                          </HStack>
                          <HStack justify="space-between">
                            <HStack>
                              <Icon as={MdOutlineCategory} color="purple.500" />
                              <Text fontSize="sm">Reformas</Text>
                            </HStack>
                            <Text fontSize="sm" fontWeight="medium">19%</Text>
                          </HStack>
                          <HStack justify="space-between">
                            <HStack>
                              <Icon as={MdOutlineCategory} color="orange.500" />
                              <Text fontSize="sm">Elétrico</Text>
                            </HStack>
                            <Text fontSize="sm" fontWeight="medium">16%</Text>
                          </HStack>
                          <HStack justify="space-between">
                            <HStack>
                              <Icon as={MdOutlineCategory} color="red.500" />
                              <Text fontSize="sm">Outros</Text>
                            </HStack>
                            <Text fontSize="sm" fontWeight="medium">11%</Text>
                          </HStack>
                        </Stack>
                      </Box>
                      
                      <Box>
                        <Text fontWeight="medium" mb={2}>Métricas Comerciais</Text>
                        <Stack spacing={3} divider={<Divider />}>
                          <Flex justify="space-between">
                            <Text fontSize="sm">Novos Usuários / Dia</Text>
                            <Text fontSize="sm" fontWeight="medium">7.9</Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text fontSize="sm">Taxa de Retenção</Text>
                            <HStack>
                              <Text fontSize="sm" fontWeight="medium">83%</Text>
                              <Badge colorScheme="green">+5%</Badge>
                            </HStack>
                          </Flex>
                          <Flex justify="space-between">
                            <Text fontSize="sm">Taxa de Churn</Text>
                            <HStack>
                              <Text fontSize="sm" fontWeight="medium">4.2%</Text>
                              <Badge colorScheme="green">-0.8%</Badge>
                            </HStack>
                          </Flex>
                          <Flex justify="space-between">
                            <Text fontSize="sm">Satisfação</Text>
                            <HStack>
                              <Text fontSize="sm" fontWeight="medium">4.7</Text>
                              <Icon as={FiStar} color="yellow.500" fontSize="sm" />
                            </HStack>
                          </Flex>
                        </Stack>
                      </Box>
                    </Grid>
                  </TabPanel>
                  
                  <TabPanel>
                    <Box mb={6}>
                      <Text fontSize="sm" color="gray.500" mb={4} textAlign="center">
                        Crescimento de Receita Mensal (últimos 6 meses)
                      </Text>
                      
                      {/* Placeholder for chart - in a real app, use Chart.js or Recharts */}
                      <Box h="150px" position="relative">
                        <Flex direction="column" h="100%" justify="flex-end">
                          <Flex h="100%" align="flex-end">
                            {['Dez', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai'].map((month, index) => (
                              <Flex 
                                key={index} 
                                direction="column" 
                                h="100%" 
                                flex="1" 
                                align="center" 
                                justify="flex-end"
                                px={1}
                              >
                                <Box
                                  w="100%"
                                  h={`${20 + (index * 10) + (index % 2 ? 5 : -5)}%`}
                                  bg={index === 5 ? 'yellow.400' : 'blue.400'}
                                  borderTopRadius="md"
                                />
                                <Text fontSize="xs" mt={2} fontWeight="medium">
                                  {month}
                                </Text>
                              </Flex>
                            ))}
                          </Flex>
                        </Flex>
                      </Box>
                    </Box>
                    
                    <Divider mb={4} />
                    
                    <Text fontWeight="medium" mb={2}>Projeções</Text>
                    <HStack spacing={4} justify="space-between">
                      <Box textAlign="center" p={3} bg={lightBg} borderRadius="md" flex="1">
                        <Text fontSize="xs" color="gray.500">Próximo Mês</Text>
                        <Text fontSize="lg" fontWeight="bold" color="blue.500">
                          +18%
                        </Text>
                      </Box>
                      <Box textAlign="center" p={3} bg={lightBg} borderRadius="md" flex="1">
                        <Text fontSize="xs" color="gray.500">Próximo Trimestre</Text>
                        <Text fontSize="lg" fontWeight="bold" color="green.500">
                          +25%
                        </Text>
                      </Box>
                      <Box textAlign="center" p={3} bg={lightBg} borderRadius="md" flex="1">
                        <Text fontSize="xs" color="gray.500">Anual</Text>
                        <Text fontSize="lg" fontWeight="bold" color="purple.500">
                          +32%
                        </Text>
                      </Box>
                    </HStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* Marketing Performance & Conversion Funnel */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "repeat(2, 1fr)" }} gap={6}>
        <Card bg={cardBg} boxShadow="md">
          <CardHeader>
            <Heading size="md">Desempenho de Marketing</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={6}>
                <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }} gap={3}>
                  <Box p={3} bg={lightBg} borderRadius="md" textAlign="center">
                    <Text fontSize="xs" color="gray.500">Cliques</Text>
                    <Text fontSize="lg" fontWeight="bold">12,450</Text>
                    <Badge colorScheme="green">+15%</Badge>
                  </Box>
                  <Box p={3} bg={lightBg} borderRadius="md" textAlign="center">
                    <Text fontSize="xs" color="gray.500">Impressões</Text>
                    <Text fontSize="lg" fontWeight="bold">125,840</Text>
                    <Badge colorScheme="green">+22%</Badge>
                  </Box>
                  <Box p={3} bg={lightBg} borderRadius="md" textAlign="center">
                    <Text fontSize="xs" color="gray.500">CTR</Text>
                    <Text fontSize="lg" fontWeight="bold">9.8%</Text>
                    <Badge colorScheme="red">-1.2%</Badge>
                  </Box>
                </Grid>
                
                <Divider />
                
                <Box>
                  <Text fontWeight="medium" mb={3}>Canais de Aquisição</Text>
                  <Stack spacing={3}>
                    <Box>
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm">Google Ads</Text>
                        <Text fontSize="sm" fontWeight="medium">32%</Text>
                      </Flex>
                      <Progress value={32} size="xs" colorScheme="blue" borderRadius="full" />
                    </Box>
                    <Box>
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm">Redes Sociais</Text>
                        <Text fontSize="sm" fontWeight="medium">28%</Text>
                      </Flex>
                      <Progress value={28} size="xs" colorScheme="purple" borderRadius="full" />
                    </Box>
                    <Box>
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm">Orgânico</Text>
                        <Text fontSize="sm" fontWeight="medium">22%</Text>
                      </Flex>
                      <Progress value={22} size="xs" colorScheme="green" borderRadius="full" />
                    </Box>
                    <Box>
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm">Referência</Text>
                        <Text fontSize="sm" fontWeight="medium">18%</Text>
                      </Flex>
                      <Progress value={18} size="xs" colorScheme="yellow" borderRadius="full" />
                    </Box>
                  </Stack>
                </Box>
                
                <Divider />
                
                <HStack justify="space-between">
                  <Box>
                    <Text fontSize="sm" color="gray.500">Custo por Aquisição</Text>
                    <Text fontSize="xl" fontWeight="bold" color="blue.500">
                      {formatCurrency(62.40)}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">ROI Marketing</Text>
                    <Text fontSize="xl" fontWeight="bold" color="green.500">
                      358%
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">Custo por Clique</Text>
                    <Text fontSize="xl" fontWeight="bold" color="purple.500">
                      {formatCurrency(0.82)}
                    </Text>
                  </Box>
                </HStack>
              </Stack>
            )}
          </CardBody>
        </Card>

        <Card bg={cardBg} boxShadow="md">
          <CardHeader>
            <Heading size="md">Funil de Conversão</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={5}>
                <Flex direction="column">
                  <Flex align="center" justify="space-between" mb={1}>
                    <HStack>
                      <Icon as={FiUsers} color="blue.500" />
                      <Text fontWeight="medium">Visitantes</Text>
                    </HStack>
                    <Text fontWeight="medium">{formatNumber(35870)}</Text>
                  </Flex>
                  <Progress value={100} size="lg" colorScheme="blue" borderRadius="full" />
                </Flex>
                
                <Flex direction="column">
                  <Flex align="center" justify="space-between" mb={1}>
                    <HStack>
                      <Icon as={FiUserPlus} color="teal.500" />
                      <Text fontWeight="medium">Cadastros</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="medium">{formatNumber(5740)}</Text>
                      <Text fontSize="sm" color="gray.500">16%</Text>
                    </HStack>
                  </Flex>
                  <Progress value={16} size="lg" colorScheme="teal" borderRadius="full" />
                </Flex>
                
                <Flex direction="column">
                  <Flex align="center" justify="space-between" mb={1}>
                    <HStack>
                      <Icon as={FiShoppingBag} color="purple.500" />
                      <Text fontWeight="medium">Cotações</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="medium">{formatNumber(3285)}</Text>
                      <Text fontSize="sm" color="gray.500">9.2%</Text>
                    </HStack>
                  </Flex>
                  <Progress value={9.2} size="lg" colorScheme="purple" borderRadius="full" />
                </Flex>
                
                <Flex direction="column">
                  <Flex align="center" justify="space-between" mb={1}>
                    <HStack>
                      <Icon as={FiCheckCircle} color="green.500" />
                      <Text fontWeight="medium">Contratações</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="medium">{formatNumber(1845)}</Text>
                      <Text fontSize="sm" color="gray.500">5.1%</Text>
                    </HStack>
                  </Flex>
                  <Progress value={5.1} size="lg" colorScheme="green" borderRadius="full" />
                </Flex>
                
                <Divider />
                
                <Flex justify="space-between" align="center">
                  <Box>
                    <Text fontSize="sm" color="gray.500">Taxa de Conversão Geral</Text>
                    <HStack>
                      <Text fontSize="xl" fontWeight="bold" color="green.500">5.1%</Text>
                      <Badge colorScheme="green">+0.8%</Badge>
                    </HStack>
                  </Box>
                  
                  <Box textAlign="right">
                    <Text fontSize="sm" color="gray.500">Valor Total Gerado</Text>
                    <Text fontSize="xl" fontWeight="bold">{formatCurrency(456789.50)}</Text>
                  </Box>
                </Flex>
              </Stack>
            )}
          </CardBody>
        </Card>
      </Grid>
    </Box>
  );
};

export default ComercialDashboard;